import type { BusinessType } from "../types";

export type ColorThemePreset = {
  id: string;
  primaryColor: string;
  secondaryColor: string;
  /** Tinte de atmósfera para el preview (hex) */
  atmosphere: string;
};

export const COLOR_THEME_PRESETS: ColorThemePreset[] = [
  {
    id: "ocean",
    primaryColor: "#0C4A6E",
    secondaryColor: "#0284C7",
    atmosphere: "#E0F2FE",
  },
  {
    id: "slate",
    primaryColor: "#0F172A",
    secondaryColor: "#475569",
    atmosphere: "#F1F5F9",
  },
  {
    id: "sunset",
    primaryColor: "#9A3412",
    secondaryColor: "#EA580C",
    atmosphere: "#FFEDD5",
  },
  {
    id: "forest",
    primaryColor: "#14532D",
    secondaryColor: "#16A34A",
    atmosphere: "#DCFCE7",
  },
  {
    id: "rose",
    primaryColor: "#9D174D",
    secondaryColor: "#DB2777",
    atmosphere: "#FCE7F3",
  },
  {
    id: "violet",
    primaryColor: "#4C1D95",
    secondaryColor: "#7C3AED",
    atmosphere: "#EDE9FE",
  },
  {
    id: "amber",
    primaryColor: "#92400E",
    secondaryColor: "#D97706",
    atmosphere: "#FEF3C7",
  },
  {
    id: "charcoal",
    primaryColor: "#18181B",
    secondaryColor: "#3F3F46",
    atmosphere: "#F4F4F5",
  },
  {
    id: "teal",
    primaryColor: "#134E4A",
    secondaryColor: "#0D9488",
    atmosphere: "#CCFBF1",
  },
  {
    id: "ink",
    primaryColor: "#15202B",
    secondaryColor: "#0F766E",
    atmosphere: "#F4F7F6",
  },
];

const SUGGESTED_BY_BUSINESS: Record<BusinessType, string[]> = {
  RESTAURANT: ["sunset", "amber", "forest", "teal"],
  BEAUTY: ["rose", "violet", "ocean", "teal"],
  TATTOO: ["ink", "charcoal", "slate", "violet"],
  GARAGE: ["slate", "charcoal", "ocean", "amber"],
  OTHER: ["ink", "ocean", "teal", "forest"],
};

export const getSuggestedThemePresets = (
  businessType: BusinessType = "OTHER",
): ColorThemePreset[] => {
  const order = SUGGESTED_BY_BUSINESS[businessType] ?? SUGGESTED_BY_BUSINESS.OTHER;
  const byId = new Map(COLOR_THEME_PRESETS.map((p) => [p.id, p]));
  const suggested = order
    .map((id) => byId.get(id))
    .filter((p): p is ColorThemePreset => Boolean(p));
  const rest = COLOR_THEME_PRESETS.filter((p) => !order.includes(p.id));
  return [...suggested, ...rest];
};

export const findMatchingPresetId = (
  primaryColor: string,
  secondaryColor: string,
): string | null => {
  const match = COLOR_THEME_PRESETS.find(
    (p) =>
      p.primaryColor.toLowerCase() === primaryColor.toLowerCase() &&
      p.secondaryColor.toLowerCase() === secondaryColor.toLowerCase(),
  );
  return match?.id ?? null;
};

export const getDefaultThemeForBusiness = (
  businessType: BusinessType = "OTHER",
): ColorThemePreset => getSuggestedThemePresets(businessType)[0];
